import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MessageCircle, Bell, Search, User, CheckCircle2 } from 'lucide-react';

// ─────────────────────────────────────────────────────────
// SHARED DATA
// ─────────────────────────────────────────────────────────
const posterFiles = [
  '/posters/Screenshot 2026-03-04 110342.png',
  '/posters/Screenshot 2026-03-04 110347.png',
  '/posters/Screenshot 2026-03-04 110353.png',
  '/posters/Screenshot 2026-03-04 110401.png',
  '/posters/Screenshot 2026-03-04 110406.png',
  '/posters/Screenshot 2026-03-04 110411.png',
  '/posters/Screenshot 2026-03-04 110418.png',
  '/posters/Screenshot 2026-03-04 110429.png',
];

const allJobs = [
  { title: 'Industrial Welder', company: 'Al Rashed Group', flag: '🇸🇦', salary: '2,800 SAR', tag: 'Urgent', type: 'intl', color: '#ef4444' },
  { title: 'Civil Foreman', company: 'Gulf Stars LLC', flag: '🇦🇪', salary: '3,200 AED', tag: 'New', type: 'intl', color: '#22c55e' },
  { title: 'Electrician', company: 'Qatar Energy Co.', flag: '🇶🇦', salary: '4,000 QAR', tag: 'Hot', type: 'intl', color: '#f59e0b' },
  { title: 'Rigger / Scaffolder', company: 'Sky Manpower', flag: '🇩🇪', salary: '€2,100/mo', tag: 'New', type: 'intl', color: '#3b82f6' },
  { title: 'Factory Electrician', company: 'Tata Projects', flag: '🇮🇳', salary: '₹28,000/mo', tag: 'New', type: 'india', color: '#f97316' },
  { title: 'Construction Mason', company: 'L&T Heavy Eng.', flag: '🇮🇳', salary: '₹22,000/mo', tag: '450+ openings', type: 'india', color: '#8b5cf6' },
  { title: 'Pipe Fitter', company: 'HPCL Refinery', flag: '🇮🇳', salary: '₹32,000/mo', tag: 'Urgent', type: 'india', color: '#ef4444' },
];

interface PhoneShowcaseProps {
  visible?: boolean;
  theme?: 'recruiter' | 'worker';
}

const JobAppScreen: React.FC<{ visible: boolean; theme: 'recruiter' | 'worker' }> = ({ visible, theme }) => {
  const [reelIndex, setReelIndex] = useState(0);
  const [phase, setPhase] = useState<'reel' | 'feed'>('reel');
  const REEL_DURATION = 1600;
  const POSTERS_TO_SHOW = posterFiles.slice(0, 5);

  const accentColor = theme === 'worker' ? 'text-amber-500' : 'text-blue-500';
  const accentBg = theme === 'worker' ? 'bg-amber-500' : 'bg-blue-500';

  useEffect(() => {
    if (!visible) return;
    if (phase !== 'reel') return;

    const timer = setInterval(() => {
      setReelIndex(prev => {
        if (prev >= POSTERS_TO_SHOW.length - 1) {
          clearInterval(timer);
          setTimeout(() => setPhase('feed'), 200);
          return prev;
        }
        return prev + 1;
      });
    }, REEL_DURATION);

    return () => clearInterval(timer);
  }, [visible, phase]);

  useEffect(() => {
    if (!visible) return;
    if (phase === 'feed') {
      const loopTimer = setTimeout(() => {
        setPhase('reel');
        setReelIndex(0);
      }, 7000);
      return () => clearTimeout(loopTimer);
    }
  }, [visible, phase]);

  useEffect(() => {
    if (!visible) {
      setPhase('reel');
      setReelIndex(0);
    }
  }, [visible]);

  return (
    <div className="absolute inset-0 bg-[#0f1117] flex flex-col overflow-hidden select-none">
      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 pt-2.5 pb-1 shrink-0">
        <span className="text-white text-[10px] font-bold">9:41</span>
        <div className="flex gap-1.5 items-center">
          <div className="flex gap-[2px] items-end h-2.5">
            {[2, 3, 4, 4].map((h, i) => (
              <div key={i} className="w-[2px] bg-white/60 rounded-full" style={{ height: `${h * 2.5}px` }} />
            ))}
          </div>
          <div className="w-4 h-2 border border-white/50 rounded-[2px] relative">
            <div className="absolute left-[1px] top-[1px] bottom-[1px] right-[3px] bg-white/60 rounded-[1px]" />
            <div className="absolute right-[-2px] top-[3px] bottom-[3px] w-[2px] bg-white/40 rounded-r-full" />
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="px-4 pt-1 pb-2.5 flex items-center justify-between shrink-0">
        <div>
          <p className="text-white/50 text-[9px] font-medium">Namaste 👋</p>
          <p className="text-white text-[15px] font-black tracking-tight">
            jobisa<span className={accentColor}>.</span>
          </p>
        </div>
        <div className="flex gap-1.5">
          <div className="w-6 h-6 bg-white/10 rounded-full flex items-center justify-center">
            <Bell size={11} className="text-white/70" />
          </div>
          <div className={`w-6 h-6 ${accentBg} rounded-full flex items-center justify-center`}>
            <User size={11} className="text-white" />
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {phase === 'reel' && (
          <motion.div
            key="reel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.98 }}
            transition={{ duration: 0.4 }}
            className="flex-1 flex flex-col px-3 pb-2 overflow-hidden"
          >
            <div className="flex gap-1 mb-2">
              {POSTERS_TO_SHOW.map((_, i) => (
                <div key={i} className="flex-1 h-[2px] bg-white/20 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-white rounded-full"
                    initial={{ width: i < reelIndex ? '100%' : '0%' }}
                    animate={{ width: i < reelIndex ? '100%' : i === reelIndex ? '100%' : '0%' }}
                    transition={i === reelIndex ? { duration: REEL_DURATION / 1000, ease: 'linear' } : { duration: 0 }}
                  />
                </div>
              ))}
            </div>

            <div className="relative flex-1 rounded-2xl overflow-hidden bg-black">
              <AnimatePresence mode="wait">
                <motion.img
                  key={reelIndex}
                  src={POSTERS_TO_SHOW[reelIndex]}
                  alt={`Poster ${reelIndex + 1}`}
                  className="absolute inset-0 w-full h-full object-contain"
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.3 }}
                />
              </AnimatePresence>
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                <p className="text-white/50 text-[8px] uppercase tracking-widest font-bold">Recruitment Poster</p>
                <p className="text-white text-[10px] font-semibold">Tap to apply • Posted by verified agency</p>
              </div>
            </div>
            <p className="text-white/30 text-[9px] text-center mt-2 font-medium">Swipe to see all posters →</p>
          </motion.div>
        )}

        {phase === 'feed' && (
          <motion.div
            key="feed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: 'spring', stiffness: 90 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="px-3 pb-2 shrink-0">
              <div className="flex items-center gap-2 bg-white/8 border border-white/10 rounded-xl px-3 py-2">
                <Search size={10} className="text-white/40" />
                <span className="text-white/30 text-[10px]">Welder, Mason, Electrician…</span>
              </div>
            </div>

            <div className="flex-1 overflow-hidden px-3 space-y-1 pb-2">
              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="flex items-center justify-between pt-1 pb-1"
              >
                <p className="text-white/60 text-[9px] font-black uppercase tracking-widest">🌍 International</p>
                <p className={`${accentColor.replace('text-', 'text-')} text-[9px] font-semibold`}>See all</p>
              </motion.div>

              {allJobs.filter(j => j.type === 'intl').map((job, i) => (
                <motion.div
                  key={job.title}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.1, duration: 0.35 }}
                  className="flex items-center gap-2 bg-white/[0.06] border border-white/[0.08] rounded-xl p-2 hover:bg-white/10 transition-colors"
                >
                  <div className="text-lg shrink-0 leading-none">{job.flag}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-[10px] font-bold truncate leading-tight">{job.title}</p>
                    <p className="text-white/40 text-[8px] truncate">{job.company}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-white/80 text-[9px] font-black">{job.salary}</p>
                    <span
                      className="inline-block text-[7px] font-bold px-1.5 py-[1px] rounded-full mt-0.5"
                      style={{ backgroundColor: job.color + '25', color: job.color }}
                    >
                      {job.tag}
                    </span>
                  </div>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="flex items-center justify-between pt-2 pb-1"
              >
                <p className="text-white/60 text-[9px] font-black uppercase tracking-widest">🇮🇳 India Jobs</p>
                <p className={`${accentColor} text-[9px] font-semibold`}>See all</p>
              </motion.div>

              {allJobs.filter(j => j.type === 'india').slice(0, 2).map((job, i) => (
                <motion.div
                  key={job.title}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.65 + i * 0.1, duration: 0.35 }}
                  className="flex items-center gap-2 bg-white/[0.06] border border-white/[0.08] rounded-xl p-2"
                >
                  <div className="text-lg shrink-0 leading-none">{job.flag}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-[10px] font-bold truncate leading-tight">{job.title}</p>
                    <p className="text-white/40 text-[8px] truncate">{job.company}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-white/80 text-[9px] font-black">{job.salary}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-t border-white/[0.08] flex items-center justify-around py-2 px-3 shrink-0">
        {[
          { icon: <Briefcase size={13} />, label: 'Jobs', active: true },
          { icon: <MessageCircle size={13} />, label: 'Chat', active: false },
          { icon: <User size={13} />, label: 'Profile', active: false },
        ].map((tab, i) => (
          <div key={i} className={`flex flex-col items-center gap-0.5 ${tab.active ? accentColor : 'text-white/25'}`}>
            {tab.icon}
            <span className="text-[7px] font-bold">{tab.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const PhoneShowcase: React.FC<PhoneShowcaseProps> = ({ visible = true, theme = 'recruiter' }) => {
  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0, y: 60 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.8, opacity: 0 }}
      transition={{ duration: 0.8, type: 'spring', stiffness: 70, damping: 15 }}
      className="relative flex items-center justify-center z-30"
    >
      {/* Ambient glow */}
      <div className={`absolute w-64 h-64 ${theme === 'worker' ? 'bg-amber-500/10' : 'bg-blue-500/20'} rounded-full blur-[100px] pointer-events-none`} />

      {/* Phone shell */}
      <div className="relative w-[280px] h-[580px] md:w-[310px] md:h-[620px]">
        {/* Metal rim */}
        <div className="absolute inset-0 rounded-[3rem] bg-slate-200 p-[1.5px] shadow-[0_30px_80px_-10px_rgba(0,0,0,0.3)]">
          <div className="relative w-full h-full rounded-[2.8rem] bg-slate-900 border-[8px] border-slate-950 overflow-hidden shadow-inner">
            <JobAppScreen visible={visible} theme={theme} />
          </div>
        </div>

        {/* Dynamic Island */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-[90px] h-[26px] bg-black rounded-full z-20" />

        {/* Side buttons */}
        <div className="absolute top-24 -left-[2px] w-[2.5px] h-11 bg-slate-300 rounded-l shadow-sm" />
        <div className="absolute top-36 -left-[2px] w-[2.5px] h-8 bg-slate-300 rounded-l shadow-sm" />
        {/* Screen reflection */}
        <div className="absolute inset-[8px] rounded-[2.2rem] bg-gradient-to-tr from-transparent via-white/[0.02] to-white/5 pointer-events-none z-10" />
      </div>
    </motion.div>
  );
};

export default PhoneShowcase;
